import React, {
    useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState
} from 'react';
import {
    AiLabContext
} from 'builders/builderInternal/context/AiLabContext';
import {
    TooltipProvider,
    Tooltip,
    TooltipTrigger,
    TooltipContent
} from 'shared/components/ui/tooltip';
import {
    Textarea
} from 'shared/components/ui/textarea';
import {
    Info
} from 'lucide-react';
import labHelper from 'builders/builderInternal/classes/AILabHelper';

const SECTION_CONFIG = [
    {
        key: 'instruction',
        heading: 'Instruction',
        placeholder: '[Provide the main instruction or task for the model]',
        tooltip: 'Describe the core task or request. Be direct so the model knows exactly what to do.'
    },
    {
        key: 'context',
        heading: 'Context',
        placeholder: '[Add any relevant background information or context]',
        tooltip: 'Share background information that can help the model understand constraints, tone, or audience.'
    },
    {
        key: 'targetFormat',
        heading: 'Target Format',
        placeholder: '[Specify the desired output format or structure]',
        tooltip: 'Explain how the answer should look—bullet list, JSON schema, prose, etc.'
    },
    {
        key: 'sampleInput',
        heading: 'Sample Input',
        placeholder: '[Provide an example input]',
        tooltip: 'Give a concrete sample input so the model can mirror the scenario you expect.'
    },
    {
        key: 'sampleOutput',
        heading: 'Sample Output',
        placeholder: '[Show the expected output for the sample input]',
        tooltip: 'Demonstrate the ideal response so quality and tone expectations are obvious.'
    }
];

const parseSectionsFromPrompt = (promptText) => {
    const parsed = labHelper.ParseStructuredPrompt(promptText);
    return {
        instruction: parsed.instruction || '',
        context: parsed.context || '',
        targetFormat: parsed.target_format || '',
        sampleInput: parsed.sample_input || '',
        sampleOutput: parsed.sample_output || ''
    };
};

const buildPromptFromSections = (sections) => {
    const blocks = SECTION_CONFIG.map(({
        key,
        heading
    }) => {
        const value = sections[key]?.trim();
        return value ? `## ${heading}\n${value}` : `## ${heading}\n`;
    });
    return blocks.join('\n\n').trim();
};

const AutoResizeTextarea = ({
    value,
    onChange,
    placeholder,
}) => {
    const MAX_HEIGHT = 400; // px cap before internal scroll
    const textareaRef = useRef(null);
    const resizeRafId = useRef(null);
    const lastHeightRef = useRef(0);

    const getScrollContainer = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return null;
        return el.closest('[data-scroll-container]');
    }, []);

    const resize = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;

        const scrollContainer = getScrollContainer();
        const prevContainerScrollTop = scrollContainer?.scrollTop ?? null;
        const prevContainerScrollLeft = scrollContainer?.scrollLeft ?? null;
        const prevTextareaScrollTop = el.scrollTop;
        const prevHeight = lastHeightRef.current || el.clientHeight;

        el.style.height = 'auto';
        const nextHeight = Math.min(
            Math.max(el.scrollHeight, prevHeight),
            MAX_HEIGHT
        );
        el.style.height = `${nextHeight}px`;
        lastHeightRef.current = nextHeight;

        if (scrollContainer) {
            if (prevContainerScrollTop !== null) scrollContainer.scrollTop = prevContainerScrollTop;
            if (prevContainerScrollLeft !== null) scrollContainer.scrollLeft = prevContainerScrollLeft;
        }
        el.scrollTop = prevTextareaScrollTop;
    }, [getScrollContainer]);

    const cancelScheduledResize = useCallback(() => {
        if (!resizeRafId.current) return;
        if (typeof window !== 'undefined' && window.cancelAnimationFrame) {
            window.cancelAnimationFrame(resizeRafId.current);
        } else {
            clearTimeout(resizeRafId.current);
        }
        resizeRafId.current = null;
    }, []);

    const scheduleResize = useCallback(() => {
        cancelScheduledResize();
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
            resizeRafId.current = window.requestAnimationFrame(resize);
        } else {
            resizeRafId.current = setTimeout(resize, 0);
        }
    }, [cancelScheduledResize, resize]);

    useLayoutEffect(() => {
        scheduleResize();
        return () => {
            cancelScheduledResize();
        };
    }, [value, scheduleResize, cancelScheduledResize]);

    const handleChange = (event) => {
        scheduleResize();
        onChange?.(event);
    };

    return (
        <Textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            rows={1}
            spellCheck={false}
            className="w-full border-none bg-transparent px-0 text-sm leading-relaxed shadow-none resize-none focus-visible:ring-0 focus-visible:outline-none placeholder:text-muted-foreground/70 [&::-webkit-scrollbar]:hidden"
            style={{
                overflowY: 'auto',
                maxHeight: `${MAX_HEIGHT}px`,
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
            }}
        />
    );
};

const AnatomyLivePrompt = ({
    actions = {}
}) => {
    const {
        state, dispatch
    } = useContext(AiLabContext);

    const instructionValue = state?.promptLab?.form?.instruction || '';
    const isLocalUpdateRef = useRef(false);

    const [sections, setSections] = useState(() => parseSectionsFromPrompt(instructionValue));

    useEffect(() => {
        if (isLocalUpdateRef.current) {
            isLocalUpdateRef.current = false;
            return;
        }
        const parsedSections = parseSectionsFromPrompt(instructionValue);
        const hasDiff = SECTION_CONFIG.some(({
            key
        }) => parsedSections[key] !== sections[key]);
        if (hasDiff) {
            setSections(parsedSections);
        }
    }, [instructionValue]);

    const sectionActions = useMemo(() => actions?.instruction || [], [actions]);

    const handleSectionChange = (sectionKey) => (event) => {
        const nextValue = event.target.value;
        setSections(prevSections => {
            const updatedSections = {
                ...prevSections,
                [sectionKey]: nextValue
            };

            isLocalUpdateRef.current = true;
            dispatch({
                type: 'UPDATE_FIELD',
                field: 'instruction',
                value: buildPromptFromSections(updatedSections)
            });

            return updatedSections;
        });
    };

    const renderSection = (section) => (
        <div
            key={section.key}
            className="flex flex-col gap-2 rounded-xl border border-transparent bg-transparent px-1"
        >
            <div className="flex items-center justify-between gap-2">
                <div className="text-[11px] font-medium uppercase tracking-widest text-foreground">
                    {section.heading}
                </div>
                <div className="flex items-center gap-2">
                    {sectionActions.length > 0 && section.key === 'instruction' && (
                        <div className="flex gap-1">
                            {sectionActions.filter(action => action.visible).map((action, index) => (
                                <button
                                    key={index}
                                    type="button"
                                    className="rounded-md border border-border/70 bg-background px-2 py-1 text-xs text-muted-foreground shadow-sm transition hover:text-foreground disabled:opacity-50"
                                    onClick={action.onClick}
                                    disabled={action.disabled}
                                >
                                    {action.icon}
                                </button>
                            ))}
                        </div>
                    )}
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                type="button"
                                className="flex h-6 w-6 items-center justify-center rounded-full border border-border/60 text-muted-foreground transition hover:text-foreground"
                                aria-label={`${section.heading} help`}
                            >
                                <Info className="h-3.5 w-3.5" />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs text-xs leading-relaxed">
                            {section.tooltip}
                        </TooltipContent>
                    </Tooltip>
                </div>
            </div>
            <AutoResizeTextarea
                value={sections[section.key] || ''}
                onChange={handleSectionChange(section.key)}
                placeholder={section.placeholder}
            />
        </div>
    );

    const instructionSection = SECTION_CONFIG.find(section => section.key === 'instruction');
    const secondarySections = SECTION_CONFIG.filter(section => section.key !== 'instruction');

    return (
        <TooltipProvider delayDuration={150}>
            <div
                className="rounded-2xl border border-border/60 bg-card/40 px-5 py-8 max-h-[75vh] overflow-y-auto [&::-webkit-scrollbar]:hidden"
                data-scroll-container
                style={{
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none'
                }}
            >
                <div className="grid gap-6 md:grid-cols-2">
                    <div className="flex flex-col gap-4">
                        {instructionSection && renderSection(instructionSection)}
                    </div>
                    <div className="flex flex-col gap-4">
                        {secondarySections.map(renderSection)}
                    </div>
                </div>
            </div>
        </TooltipProvider>
    );
};

export default AnatomyLivePrompt;